import { Edit3, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { ConfirmDialog } from "@shared/components/ConfirmDialog";
import { errMsg } from "@shared/api/errors";
import {
  useCommercialSettings,
  useDeleteCommercialLicense,
  useSaveCommercialLicense,
  useUpdateCommercialSettings,
} from "../api";
import { CommercialHeader } from "../components/CommercialHeader";
import { useCommercialStore } from "../store";
import type { CommercialLicense } from "../types";

type ConfirmState =
  | { type: "settings" }
  | { type: "license-edit"; license: CommercialLicense }
  | { type: "license-delete"; license: CommercialLicense }
  | null;

export function CommercialSettingsPage() {
  const { emisorId } = useCommercialStore();
  const { data, isLoading } = useCommercialSettings(emisorId);
  const updateSettings = useUpdateCommercialSettings();
  const saveLicense = useSaveCommercialLicense();
  const deleteLicense = useDeleteCommercialLicense();

  const [tax, setTax] = useState(23);
  const [currency, setCurrency] = useState("EUR");
  const [licenseModal, setLicenseModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState<CommercialLicense | null>(null);
  const [licenseName, setLicenseName] = useState("");
  const [licensePct, setLicensePct] = useState(0);
  const [licenseActive, setLicenseActive] = useState(true);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [error, setError] = useState<string | null>(null);

  const busy = updateSettings.isPending || saveLicense.isPending || deleteLicense.isPending;
  const pendingMessage = updateSettings.isPending
    ? "Guardando configuración…"
    : saveLicense.isPending
      ? editingLicense ? "Guardando licencia…" : "Creando licencia…"
      : deleteLicense.isPending
        ? "Eliminando licencia…"
        : null;

  useEffect(() => {
    if (!data) return;
    setTax(data.configuracion.impuesto_pct);
    setCurrency(data.configuracion.moneda);
  }, [data]);

  function openLicense(license?: CommercialLicense) {
    if (busy) return;
    setEditingLicense(license ?? null);
    setLicenseName(license?.nombre ?? "");
    setLicensePct(license?.porcentaje ?? 0);
    setLicenseActive(license?.activo ?? true);
    setError(null);
    setLicenseModal(true);
  }

  async function persistSettings() {
    if (!emisorId || updateSettings.isPending) return;
    setError(null);
    try {
      await updateSettings.mutateAsync({ emisorId, payload: { impuesto_pct: tax, moneda: currency.toUpperCase() } });
    } catch (e) {
      setError(errMsg(e, "No se pudo guardar la configuración."));
    }
  }

  async function persistLicense() {
    if (!emisorId || saveLicense.isPending) return;
    setError(null);
    try {
      await saveLicense.mutateAsync({
        emisorId,
        id: editingLicense?.id,
        payload: { nombre: licenseName.trim(), porcentaje: licensePct, activo: licenseActive },
      });
      setLicenseModal(false);
      setEditingLicense(null);
    } catch (e) {
      setError(errMsg(e, "No se pudo guardar la licencia."));
    }
  }

  function submitLicense(e: React.FormEvent) {
    e.preventDefault();
    if (editingLicense) {
      setConfirm({ type: "license-edit", license: editingLicense });
    } else {
      void persistLicense();
    }
  }

  async function runConfirm() {
    if (!confirm || !emisorId) return;
    if (confirm.type === "settings") await persistSettings();
    if (confirm.type === "license-edit") await persistLicense();
    if (confirm.type === "license-delete") {
      try {
        await deleteLicense.mutateAsync({ emisorId, id: confirm.license.id });
      } catch (e) {
        setError(errMsg(e, "No se pudo eliminar la licencia."));
      }
    }
  }

  const copy = confirm?.type === "license-delete"
    ? { title: "Eliminar licencia", message: `¿Eliminar definitivamente la licencia “${confirm.license.nombre}”? Las valoraciones históricas conservarán su snapshot.`, label: "Eliminar", destructive: true }
    : confirm?.type === "license-edit"
      ? { title: "Guardar edición de licencia", message: `¿Confirmas los cambios de “${confirm.license.nombre}”?`, label: "Guardar cambios", destructive: false }
      : { title: "Guardar configuración comercial", message: "¿Confirmas el nuevo porcentaje de impuestos y la moneda de esta empresa?", label: "Guardar cambios", destructive: false };

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <CommercialHeader title="Configuración" subtitle="Impuestos, moneda y licencias específicas de la empresa seleccionada." />
      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{error}</div>}
      {pendingMessage && <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800"><Loader2 className="animate-spin" size={17} />{pendingMessage}</div>}

      {isLoading ? <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm font-semibold text-slate-500">Cargando configuración…</div> : (
        <div className="grid gap-6 xl:grid-cols-[.65fr_1.35fr]">
          <section className="h-fit rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-black text-slate-900">Parámetros generales</h2>
            <p className="mt-1 text-sm text-slate-500">Se aplican a las nuevas valoraciones y quedan congelados al guardar.</p>
            <div className="mt-5 space-y-4">
              <label className="block text-xs font-black uppercase tracking-wide text-slate-500">Impuestos (%)<input type="number" min="0" max="100" step="0.01" value={tax} onChange={(e) => setTax(Number(e.target.value))} className={inputClass} /></label>
              <label className="block text-xs font-black uppercase tracking-wide text-slate-500">Moneda<input maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className={inputClass} /></label>
            </div>
            <button type="button" onClick={() => setConfirm({ type: "settings" })} disabled={busy} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-800 disabled:cursor-wait disabled:opacity-50">{updateSettings.isPending ? <Loader2 className="animate-spin" size={17} /> : <Save size={17} />}{updateSettings.isPending ? "Guardando…" : "Guardar configuración"}</button>
          </section>

          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4"><div><h2 className="font-black text-slate-900">Licencias</h2><p className="text-sm text-slate-500">Porcentaje adicional aplicado sobre el total con beneficio.</p></div><button type="button" onClick={() => openLicense()} disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-800 disabled:cursor-wait disabled:opacity-50"><Plus size={17} />Nueva licencia</button></div>
            <div className="overflow-x-auto"><table className="w-full min-w-[650px] text-sm"><thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-5 py-3">Nombre</th><th className="px-5 py-3 text-right">Porcentaje</th><th className="px-5 py-3">Estado</th><th className="px-5 py-3 text-right">Acciones</th></tr></thead><tbody className="divide-y divide-slate-100">{data?.licencias.length ? data.licencias.map((license) => <tr key={license.id}><td className="px-5 py-3 font-semibold text-slate-800">{license.nombre}</td><td className="px-5 py-3 text-right font-bold">{license.porcentaje.toFixed(2)}%</td><td className="px-5 py-3"><span className={license.activo ? "rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-black text-emerald-700" : "rounded-full bg-slate-100 px-2.5 py-1 text-xs font-black text-slate-600"}>{license.activo ? "Activa" : "Inactiva"}</span></td><td className="px-5 py-3"><div className="flex justify-end gap-1"><button type="button" onClick={() => openLicense(license)} disabled={busy} className="rounded-lg p-2 text-blue-700 hover:bg-blue-50 disabled:cursor-wait disabled:opacity-40" title="Editar"><Edit3 size={16} /></button><button type="button" onClick={() => setConfirm({ type: "license-delete", license })} disabled={busy} className="rounded-lg p-2 text-rose-700 hover:bg-rose-50 disabled:cursor-wait disabled:opacity-40" title="Eliminar">{deleteLicense.isPending && deleteLicense.variables?.id === license.id ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}</button></div></td></tr>) : <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500">No hay licencias configuradas.</td></tr>}</tbody></table></div>
          </section>
        </div>
      )}

      {licenseModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onMouseDown={(e) => e.target === e.currentTarget && !saveLicense.isPending && setLicenseModal(false)}><form onSubmit={submitLicense} className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl"><div className="flex items-center justify-between"><div><h2 className="text-xl font-black text-slate-900">{editingLicense ? "Editar licencia" : "Nueva licencia"}</h2><p className="text-sm text-slate-500">Configuración específica de la empresa actual.</p></div><button type="button" onClick={() => setLicenseModal(false)} disabled={saveLicense.isPending} className="rounded-lg p-2 hover:bg-slate-100 disabled:cursor-wait disabled:opacity-40"><X size={20} /></button></div><div className="mt-5 space-y-4"><label className="block text-xs font-black uppercase tracking-wide text-slate-500">Nombre<input required value={licenseName} onChange={(e) => setLicenseName(e.target.value)} className={inputClass} /></label><label className="block text-xs font-black uppercase tracking-wide text-slate-500">Porcentaje (%)<input required type="number" min="0" step="0.01" value={licensePct} onChange={(e) => setLicensePct(Number(e.target.value))} className={inputClass} /></label><label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm font-bold text-slate-700"><input type="checkbox" checked={licenseActive} onChange={(e) => setLicenseActive(e.target.checked)} className="h-4 w-4" />Licencia activa</label></div><div className="mt-6 flex justify-end gap-2"><button type="button" onClick={() => setLicenseModal(false)} disabled={saveLicense.isPending} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-black text-slate-700 disabled:cursor-wait disabled:opacity-50">Cancelar</button><button type="submit" disabled={saveLicense.isPending} className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-800 disabled:cursor-wait disabled:opacity-50">{saveLicense.isPending && <Loader2 className="animate-spin" size={16} />}{saveLicense.isPending ? editingLicense ? "Guardando…" : "Creando…" : editingLicense ? "Guardar cambios" : "Crear licencia"}</button></div></form></div>}

      <ConfirmDialog open={confirm !== null} title={copy.title} message={copy.message} confirmLabel={copy.label} destructive={copy.destructive} onConfirm={() => void runConfirm()} onClose={() => setConfirm(null)} />
    </div>
  );
}

const inputClass = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15";
